import { Avatar, Tag } from 'antd';
import CircularProgress from 'components/CircularProgress';
import { selectValueSeparator } from 'constants/selectValueSeparator';
import useInfiniteDataQuery from 'hooks/useInfiniteDataQuery';
import React, { useEffect, useState } from 'react';
import { getRoleTagProps } from 'routes/Users/helpers';
import { GET_NOTARY_FOR_TRANSACTION_TYPES_PRICES } from 'services/graphql/queries/user.query';
import { IUser, UserStatusEnum } from 'types/user';
import { getDefaultAvatarUrl } from 'utils';

import { ITransactionTypeTabsProps } from '../../../types';
import CommonTransactionTypePriceStructure from '../CommonTransactionTypePriceStructure';
import TransactionTypeSpecificsInput from './TransactionTypeSpecificsInput';
import { IPriceSettingsAsReactState, OnPriceChangeFuncType, OnPriceDeleteFuncType } from './types';

interface IPriceSettingSubmitValue {
  priceRON?: number[];
  priceInPerson?: number[];
  user: string[];
}

// const customizer = (objValue: any[], srcValue: any[]): any[] => {
//   if (isArray(objValue)) {
//     return objValue.concat(srcValue);
//   }
//   return [];
// };

const NotarySpecificPrice: React.FC<ITransactionTypeTabsProps> = ({ activeTransactionType, onSubmit, formRef }) => {
  const [deletedPriceSetting, setDeletedPriceSetting] = useState<Record<string, boolean>>();
  const [newPriceSettings, setNewPriceSettings] = useState<Record<string, IPriceSettingsAsReactState>>();

  const userSelectOptionsMapper = (user: IUser) => {
    const tagProps = getRoleTagProps(user.role);
    const userFullName = `${user.firstName} ${user.lastName}`;
    return {
      value: `${userFullName}${selectValueSeparator}${user.id}`,
      label: (
        <div className='gx-d-flex gx-align-items-center'>
          <Avatar
            style={{ width: 25, height: 25 }}
            className='gx-mr-2'
            src={user.avatar?.thumbnailPipeFrom || getDefaultAvatarUrl(userFullName)}
          />
          <div>{`${user.firstName} ${user.lastName}`}</div>
          <div style={{ margin: '0 5px' }}>-</div>
          <Tag className='gx-m-0' color={tagProps.color}>
            {tagProps.text}
          </Tag>
        </div>
      ),
    };
  };

  const notExistsUuid = 'e9163554-052e-4fee-a427-c40e2eab1a3a';
  const { loading, fetchNextPageData, data: users, fetchByQuery } = useInfiniteDataQuery({
    graphQLQuery: GET_NOTARY_FOR_TRANSACTION_TYPES_PRICES,
    optionMapper: userSelectOptionsMapper,
    queryProps: {
      variables: {
        transactionType: activeTransactionType?.id || notExistsUuid,
        status: UserStatusEnum.ACTIVE,
      },
    },
  });

  // useEffect(() => {
  //   setPriceSettings(cloneDeep(activeTransactionType?.allUserPrice));
  // }, [activeTransactionType?.allUserPrice]);

  useEffect(() => {
    if (!activeTransactionType) {
      setDeletedPriceSetting({});
      setNewPriceSettings(undefined);
    }
  }, [activeTransactionType]);

  // const onInitPriceSettingsChange: OnPriceChangeFuncType = (value, selectedUserId, isRON) => {
  //   setPriceSettings((c) => {
  //     const current = { ...(c || {}) };
  //     const key = isRON ? 'priceRON' : 'priceInPerson';
  //     current[selectedUserId][key] = parseFloat(value);
  //     return current;
  //   });
  // };

  const onDeleteInitPriceSettings: OnPriceDeleteFuncType = (userId) => {
    setDeletedPriceSetting((c) => ({ ...c, [userId]: true }));
  };

  const onNewPriceSettingsChange: OnPriceChangeFuncType = (value, selectedUserId, isRON) => {
    setNewPriceSettings((c) => {
      const current = { ...(c || {}) };
      const key = isRON ? 'priceRON' : 'priceInPerson';
      if (!current[selectedUserId]) {
        current[selectedUserId] = {};
      }
      current[selectedUserId][key] = parseFloat(value);
      return current;
    });
    onDeleteInitPriceSettings(selectedUserId);
  };

  const onDeleteNewPriceSettings: OnPriceDeleteFuncType = (userId) => {
    setNewPriceSettings((c) => {
      const current = { ...(c || {}) };
      delete current[userId];
      return current;
    });
  };

  // const onSelectUser = (v: IUser | undefined) => {
  //   setSelectedUser(v);
  // };

  const handleSubmit = () => {
    const submitPriceValues = users?.reduce(
      (acc: IPriceSettingSubmitValue, user: IUser) => {
        const userId = user.id;
        const existedUserPrice = deletedPriceSetting?.[userId] ? undefined : user.specificPrices?.[0];
        const submitUserPrice = newPriceSettings?.[userId] || existedUserPrice;
        if (submitUserPrice) {
          acc.priceInPerson?.push(submitUserPrice.priceInPerson || 0);
          acc.priceRON?.push(submitUserPrice.priceRON || 0);
          acc.user?.push(userId);
        }
        return acc;
      },
      { priceRON: [], priceInPerson: [], user: [] },
    );
    onSubmit?.(submitPriceValues);
  };

  // eslint-disable-next-line no-param-reassign
  formRef.current = { handleSubmit };

  const handleAddSpecificPriceClick = (newSpecificPriceUser?: IUser) => {
    if (newSpecificPriceUser)
      setNewPriceSettings((c) => ({
        ...(c || {}),
        [newSpecificPriceUser.id]: {
          priceInPerson: 0,
          priceRON: 0,
          user: newSpecificPriceUser,
        },
      }));
  };

  return (
    <CommonTransactionTypePriceStructure
      onSearch={fetchByQuery}
      searchPlaceholder='Search User...'
      fetchMoreData={fetchNextPageData}>
      <>
        {loading && !(!!users && users.length > 0) && <CircularProgress />}
        {users?.map((user: IUser, index) => {
          const userId = user.id;
          const initPriceSetting = user.specificPrices?.[0];
          const isUserWithExistedTransactionType = !!initPriceSetting && !deletedPriceSetting?.[userId];
          const priceDetail = isUserWithExistedTransactionType ? initPriceSetting : undefined;
          const newPriceDetail = newPriceSettings?.[userId];
          return (
            <TransactionTypeSpecificsInput
              key={userId}
              isVisible
              index={index}
              onPriceChange={onNewPriceSettingsChange}
              onCreateNewPriceClick={handleAddSpecificPriceClick}
              userDetail={user}
              priceDetail={newPriceDetail || priceDetail}
              onDelete={isUserWithExistedTransactionType ? onDeleteInitPriceSettings : onDeleteNewPriceSettings}
            />
          );
        })}
        {loading && !!users && !!users.length && (
          <div>
            <CircularProgress />
          </div>
        )}
      </>
    </CommonTransactionTypePriceStructure>
  );
};

export default React.memo(NotarySpecificPrice);
